import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, PATCH_URL } from "../data/content";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (e, href) => {
    e.preventDefault();
    setOpen(false);
    const scroll = () => {
      const el = document.querySelector(href);
      if (!el) return;
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
      else el.scrollIntoView({ behavior: "smooth" });
    };
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(scroll, 150);
    } else {
      scroll();
    }
  };

  return (
    <header
      data-testid="nav-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color] duration-500 border-b ${
        scrolled ? "bg-abyss/90 backdrop-blur-md border-polar/15" : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12 h-[68px] flex items-center justify-between">
        <a href="/" onClick={(e) => goTo(e, "#home")} className="flex items-center gap-3 group" data-testid="nav-brand">
          <img src={PATCH_URL} alt="Arjuna Antarctica mission patch" className="w-9 h-9 rounded-full ring-1 ring-gold/50" />
          <div className="leading-none">
            <span className="font-display font-bold tracking-wide text-ice text-lg block">ARJUNA</span>
            <span className="font-mono text-[9px] tracking-[0.25em] text-polar block">ANTARCTICA · PROPOSED</span>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => goTo(e, l.href)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="font-mono text-[11px] uppercase tracking-[0.18em] text-tech hover:text-polar transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/updates"
            data-testid="nav-updates-link"
            className="font-mono text-[11px] uppercase tracking-[0.18em] text-tech hover:text-polar transition-colors duration-300"
          >
            Updates
          </Link>
          <Link
            to="/ocean"
            data-testid="nav-ocean-link"
            className="font-mono text-[11px] uppercase tracking-[0.18em] text-polar hover:text-ice transition-colors duration-300"
          >
            Indian Ocean →
          </Link>
          <a
            href="#partners"
            onClick={(e) => goTo(e, "#partners")}
            data-testid="nav-partner-cta"
            className="font-mono text-[11px] uppercase tracking-[0.18em] px-4 py-2 bg-mission text-ice hover:bg-ice hover:text-midnight transition-colors duration-300 clip-corner"
          >
            Partner With the Mission
          </a>
        </nav>

        <button
          className="lg:hidden text-ice p-2"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-label={open ? "Close menu" : "Open menu"}
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-abyss border-b border-polar/15"
            aria-label="Mobile"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => goTo(e, l.href)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-display text-2xl font-semibold text-ice hover:text-polar transition-colors"
                >
                  {l.label}
                </a>
              ))}
              <Link to="/updates" onClick={() => setOpen(false)} className="font-display text-2xl font-semibold text-ice hover:text-polar transition-colors" data-testid="nav-mobile-updates-link">
                Updates
              </Link>
              <Link to="/ocean" onClick={() => setOpen(false)} className="font-display text-2xl font-semibold text-polar hover:text-ice transition-colors" data-testid="nav-mobile-ocean-link">
                Indian Ocean Expedition →
              </Link>
              <a
                href="#partners"
                onClick={(e) => goTo(e, "#partners")}
                data-testid="nav-mobile-partner-cta"
                className="mt-2 font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 bg-mission text-ice text-center clip-corner"
              >
                Partner With the Mission
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
